<script lang="ts">
  import type { StatusTone } from '@clinic/contracts';
  import { Check, Circle, CircleDot, TriangleAlert, X } from '@lucide/svelte';
  import { cn } from '../lib/cn';

  /**
   * The one status language (ADR-016). Callers map a state to a tone only through the STATUS_TONE
   * tables in @clinic/contracts. Each tone has its own shape as well as its colour, and the label is
   * always there (written out, or as the accessible name of the compact form), so colour is never the
   * only signal. «In progress» is steel, never the action colour.
   */
  const TONES = {
    neutral: { box: 'border-border-strong bg-surface-3 text-text-2', Icon: Circle },
    progress: { box: 'border-info-line bg-info-soft text-info', Icon: CircleDot },
    attention: { box: 'border-warn-line bg-warn-soft text-warn', Icon: TriangleAlert },
    success: { box: 'border-success-line bg-success-soft text-success', Icon: Check },
    failure: { box: 'border-danger-line bg-danger-soft text-danger', Icon: X },
  } satisfies Record<StatusTone, unknown>;

  let {
    tone,
    label,
    class: className,
    compact = false,
  }: {
    tone: StatusTone;
    label: string;
    class?: string;
    /** Icon only (a small calendar block); the label stays as its accessible name and tooltip. */
    compact?: boolean;
  } = $props();
  const t = $derived(TONES[tone]);
</script>

{#if compact}
  <span
    role="img"
    aria-label={label}
    title={label}
    class={cn('inline-flex size-4 shrink-0 items-center justify-center rounded-full border', t.box, className)}
  >
    <t.Icon class="size-2.5" aria-hidden="true" strokeWidth={3} />
  </span>
{:else}
  <span class={cn('inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium tabular-nums', t.box, className)}
    ><t.Icon class="size-3" aria-hidden="true" strokeWidth={2.5} />{label}</span
  >
{/if}
